import { CommonModule, Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, HostBinding, inject, Input, OnInit, Renderer2 } from '@angular/core';
import { NavigationEnd } from '@angular/router';
import { Meta } from '@df/core/meta/meta';
import { BodyClassService } from '@df/ui/body-class.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { UiComponent } from '@df/ui/ui.component';

@Component({
    selector: 'back-to-top',
    templateUrl: './back-to-top.html',
    styles: [
        `
            :host:not(.initialized) {
                display: none;
            }
        `
    ],
    standalone: true,
    imports: [CommonModule, TranslatePipe],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BackToTopComponent extends UiComponent implements OnInit {
    /**
     * The height at which the page starts
     * @type {number}
     */
    @Input()
    top = 0;

    protected forbiddenLocations: string[] = [];

    protected location = inject(Location);
    protected bodyClassService = inject(BodyClassService);

    @HostBinding('class.initialized')
    private initializedClass = false;

    backToTop!: () => void;
    update!: () => void;
    private _canShow?: boolean;

    protected elementRef = inject(ElementRef);
    protected renderer = inject(Renderer2);
    protected meta = inject(Meta);

    override ngOnInit() {
        this.backToTop = this.timeoutService.throttle(() => {
            const scrollingElement = this.getScrollingElement();

            if (scrollingElement) {
                scrollingElement.scrollTo({
                    top: 0,
                    left: 0
                });
            }
        }, 1000);

        this.update = this.timeoutService.throttle(() => this.onUpdate(), 100);

        this.subscriptions.add(this.client.obs.scroll.subscribe(() => this.update()));
        this.subscriptions.add(
            this.meta.routerEvents.subscribe(routerEvent => {
                if (routerEvent instanceof NavigationEnd) {
                    this.update();
                }
            })
        );

        this.initializedClass = true;
        this.update();

        super.ngOnInit();
    }

    protected getScrollingElement(): Element {
        return this.client.getRootElement();
    }

    /**
     * Display component if you are not in forbidden location
     */
    get canShow(): boolean {
        if (this.forbiddenLocations?.includes(this.location.path())) {
            return false;
        }

        if (typeof document !== 'undefined') {
            const scrollingElement = this.getScrollingElement();

            return !!(scrollingElement && scrollingElement.scrollTop > (this.top || 0));
        }

        return false;
    }

    onUpdate(): void {
        if (this._canShow === undefined || this._canShow !== this.canShow) {
            if (!this.canShow) {
                this.renderer.addClass(this.elementRef.nativeElement, 'ng-hide');
            } else {
                this.renderer.removeClass(this.elementRef.nativeElement, 'ng-hide');
            }

            this.bodyClassService.set('is-back-to-top', this, this.canShow);

            this._canShow = this.canShow;
        }
    }
}
